import PropTypes from 'prop-types';

import {
  Box,
  Heading,
  Skeleton,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';

/**
 * @override
 * CategoryCardGrid Contentful model.
 *
 * Renders a responsive grid of Card items:
 *   - 2 columns on mobile
 *   - 3 columns on desktop
 *
 * Layout is CSS-grid driven via the theme; no inline styling props.
 */
export const CategoryCardGrid = ({component, isComplete = true, isFetching = false, ...props}) => {
  const styles = useMultiStyleConfig('contentful/category-card-grid', {
    component,
    isComplete,
    isFetching,
  });

  const items =
    component.cardItemsCollection?.items ??
    component.cardItemsCursorCollection?.items ??
    [];

  const validItems = items.filter(Boolean);
  const headingText = typeof component.title === 'string' ? component.title.trim() : '';

  const sectionHeading = headingText ? (
    <Box className="contentful-category-card-grid__heading" {...styles.sectionHeading}>
      <Heading as="h2" className="contentful-category-card-grid__title" {...styles.sectionTitle}>
        {headingText}
      </Heading>
    </Box>
  ) : null;

  if (!isComplete && isFetching) {
    return (
      <Box className="contentful-category-card-grid__root" {...styles.root} {...props}>
        {headingText ?
          sectionHeading :
          <Skeleton height={{base: '28px', lg: '40px'}} width="55%" />}
        <Box className="contentful-category-card-grid__grid" {...styles.grid}>
          {Array.from({length: 3}).map((_, i) => (
            <Box key={i} className="contentful-category-card-grid__grid-item" {...styles.gridItem}>
              <Skeleton aspectRatio="424 / 535" width="100%" />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (!validItems.length) return null;

  return (
    <Box className="contentful-category-card-grid__root" {...styles.root} {...props}>
      {sectionHeading}
      <Box className="contentful-category-card-grid__grid" {...styles.grid}>
        {validItems.map((item) => (
          <Box
            key={item.sys?.id ?? item.productId ?? item.title}
            className="contentful-category-card-grid__grid-item"
            {...styles.gridItem}
          >
            <Component component={item} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

CategoryCardGrid.displayName = 'CategoryCardGrid';
CategoryCardGrid.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
